import { createApiInstance } from './ApiConfig';

// Create axios instance with base configuration plus specific settings
const api = createApiInstance({
  withCredentials: false,
  timeout: 30000, // 30 seconds default timeout
  headers: {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Credentials': 'true',
  },
});

// Management routes are scoped per Android package name
const appPath = packageName => `/app/ota/apps/${encodeURIComponent(packageName)}`;

// App registry APIs
export const listApps = async () => {
  return api.get('/app/ota/apps');
};

export const getApp = async packageName => {
  return api.get(appPath(packageName));
};

export const createApp = async (packageName, displayName = null) => {
  return api.post('/app/ota/apps', {
    package_name: packageName,
    display_name: displayName,
  });
};

export const updateApp = async (packageName, displayName) => {
  return api.put(appPath(packageName), { display_name: displayName });
};

export const deleteApp = async packageName => {
  return api.delete(appPath(packageName));
};

// Channel Management APIs
export const createChannel = async (packageName, channelData) => {
  return api.post(`${appPath(packageName)}/channels`, channelData);
};

export const listChannels = async packageName => {
  return api.get(`${appPath(packageName)}/channels`);
};

export const updateChannelVersion = async (packageName, channelName, versioncode) => {
  return api.put(
    `${appPath(packageName)}/channels/${channelName}/version`,
    { versioncode },
    {
      params: { versioncode },
    }
  );
};

export const renameChannel = async (packageName, oldName, newName) => {
  return api.put(`${appPath(packageName)}/channels/rename`, {
    old_name: oldName,
    new_name: newName,
  });
};

export const deleteChannel = async (packageName, channelName) => {
  return api.delete(`${appPath(packageName)}/channels/${channelName}`);
};

// APK Management APIs
// Upload is not scoped: the backend files the APK under the package name read
// from its manifest and registers that app on first upload.
export const uploadApk = async (file, onUploadProgress = null) => {
  const formData = new FormData();
  formData.append('file', file);

  return api.post('/app/ota/apk/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 300000, // 5 minutes timeout for APK upload
    onUploadProgress: onUploadProgress,
  });
};

export const listApks = async packageName => {
  return api.get(`${appPath(packageName)}/apk/list`);
};

export const downloadApk = async (packageName, versioncode) => {
  return api.get(`/app/ota/download/${versioncode}`, {
    params: { package_name: packageName },
    responseType: 'blob',
  });
};

export const deleteApk = async (packageName, versioncode) => {
  return api.delete(`${appPath(packageName)}/apk/${versioncode}`);
};

export const checkApkExists = async (packageName, versioncode) => {
  return api.get(`${appPath(packageName)}/apk/${versioncode}/exists`);
};

export const checkApkReferences = async (packageName, versioncode) => {
  return api.get(`${appPath(packageName)}/apk/${versioncode}/references`);
};

// Device Management APIs
export const setDeviceChannel = async (packageName, deviceId, channelName) => {
  return api.post(`${appPath(packageName)}/devices`, {
    device_id: deviceId,
    channel_name: channelName,
  });
};

export const listDeviceMappings = async packageName => {
  return api.get(`${appPath(packageName)}/devices`);
};

export const removeDeviceMapping = async (packageName, deviceId) => {
  return api.delete(`${appPath(packageName)}/devices/${deviceId}`);
};

// Nicknames belong to the device and are shared by every app installed on it
export const setDeviceNickname = async (deviceId, nickname) => {
  return api.put(
    `/app/ota/devices/${deviceId}/nickname`,
    { nickname },
    {
      params: { nickname },
    }
  );
};

export const getDevicesWithNicknames = async packageName => {
  return api.get(`${appPath(packageName)}/devices/with-nicknames`);
};

// Version Statistics APIs
export const getVersionStatistics = async packageName => {
  return api.get(`${appPath(packageName)}/version-stats`);
};

// Patch Management APIs
export const listPatches = async packageName => {
  return api.get(`${appPath(packageName)}/patches/list`);
};

export const deletePatch = async (packageName, oldVersion, newVersion) => {
  return api.delete(`${appPath(packageName)}/patches/${oldVersion}/${newVersion}`);
};

export const getRunningPatchTasks = async packageName => {
  return api.get(`${appPath(packageName)}/patches/running`);
};

export const generatePatch = async (packageName, oldVersion, newVersion) => {
  return api.post(`${appPath(packageName)}/patches/generate`, null, {
    params: { old_version: oldVersion, new_version: newVersion },
  });
};

// Every device known to the OTA service across all apps (nickname, linked hub /
// machine, company, per-app state). Feeds the device picker.
export const listKnownDevices = async () => {
  return api.get('/app/ota/devices');
};
